import React, { useState, useEffect } from 'react';
import Card from '../../ui/Card';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Lightbox from '../../ui/Lightbox';
import { CalendarIcon } from '../../ui/Icons';
import { DocumentTextIcon, ExclamationTriangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import communiquesData from '../../../data/communiquesData.json';

export default function CommuniquesFeed() {
  const [communiques, setCommuniques] = useState(() =>
    [...communiquesData].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
  );
  const [showAll, setShowAll] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    localStorage.setItem('acem_communiques_consulted', 'true');

    fetch(`https://raw.githubusercontent.com/acemfes/acem_fes_communiques/main/communiquesData.json?t=${Date.now()}`)
      .then((res) => res.json())
      .then((data) => {
        data.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setCommuniques(data);
      })
      .catch((err) => console.error("Erreur chargement GitHub communiqués:", err));
  }, []);

  const displayedItems = showAll ? communiques : communiques.slice(0, 3);
  const hasMore = communiques.length > 3;

  return (
    <div className="max-w-6xl mx-auto space-y-8 px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayedItems.map((item) => (
          <Card
            key={item.id}
            variant="elevated"
            className="p-5 flex flex-col justify-between gap-4"
          >
            {/* Header info */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Badge variant="primary">
                    {item.categorie}
                  </Badge>
                  {item.important && (
                    <Badge variant="warning">
                      <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1 inline-block" aria-hidden="true" />
                      Important
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-acem-muted font-medium flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5 text-acem-primary" />
                  {item.dateFormatee || item.date}
                </span>
              </div>

              <h3 className="font-title text-base sm:text-lg font-bold text-acem-primary-dark leading-snug">
                {item.titre}
              </h3>

              {/* Description (if no image) */}
              {item.description && (!item.imageFiles || item.imageFiles.length === 0) && !item.imageUrl && (
                <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed">
                  {item.description}
                </p>
              )}

              {/* Images preview */}
              {((item.imageFiles && item.imageFiles.length > 0) || item.imageUrl) && (
                <div className="space-y-2 pt-2">
                  {item.imageFiles && item.imageFiles.length > 0 ? (
                    item.imageFiles.map((img, imgIdx) => {
                      const imgSrc = `/communiques/${img}`;
                      const githubSrc = `https://raw.githubusercontent.com/acemfes/acem_fes_communiques/main/images/${encodeURIComponent(img)}`;
                      return (
                        <div
                          key={imgIdx}
                          onClick={(e) => {
                            const imgEl = e.currentTarget.querySelector('img');
                            const activeSrc = (imgEl && imgEl.currentSrc) || (imgEl && imgEl.src) || githubSrc || imgSrc;
                            setSelectedImage(activeSrc);
                          }}
                          className="rounded-xl overflow-hidden cursor-pointer bg-acem-surface-container-low border border-acem-border group relative aspect-[4/3] flex items-center justify-center"
                          title="Cliquer pour agrandir"
                        >
                          <img
                            src={imgSrc}
                            alt={`${item.titre} ${imgIdx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              if (e.currentTarget.src !== githubSrc) {
                                e.currentTarget.src = githubSrc;
                              }
                            }}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div
                      onClick={() => setSelectedImage(item.imageUrl)}
                      className="rounded-xl overflow-hidden cursor-pointer bg-acem-surface-container-low border border-acem-border group relative aspect-[4/3] flex items-center justify-center"
                      title="Cliquer pour agrandir"
                    >
                      <img
                        src={item.imageUrl}
                        alt={item.titre}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PDF attachments */}
            {((item.pdfFiles && item.pdfFiles.length > 0) || item.pdfUrl) && (
              <div className="pt-2 border-t border-acem-border/50 flex flex-wrap gap-2">
                {item.pdfFiles && item.pdfFiles.length > 0 ? (
                  item.pdfFiles.map((pdf, pdfIdx) => (
                    <Button
                      key={pdfIdx}
                      href={`/communiques/${pdf}`}
                      target="_blank"
                      rel="noreferrer"
                      variant="primary"
                      size="sm"
                      className="flex items-center gap-1.5"
                    >
                      <DocumentTextIcon className="w-4 h-4" aria-hidden="true" />
                      Consulter le PDF
                    </Button>
                  ))
                ) : (
                  <Button
                    href={item.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="primary"
                    size="sm"
                    className="flex items-center gap-1.5"
                  >
                    <DocumentTextIcon className="w-4 h-4" aria-hidden="true" />
                    Consulter le PDF
                  </Button>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Voir plus button */}
      {hasMore && (
        <div className="text-center pt-4">
          <Button
            onClick={() => setShowAll(!showAll)}
            variant="primary"
            size="md"
            className="flex items-center justify-center gap-1.5 mx-auto"
          >
            <span>{showAll ? "Réduire l'affichage (3 récents)" : "Voir tous les communiqués"}</span>
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAll ? 'rotate-180' : ''}`} aria-hidden="true" />
          </Button>
        </div>
      )}

      {/* Mutualized Lightbox */}
      {selectedImage && (
        <Lightbox
          src={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
}
